import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { 
  Menu, 
  X, 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  Receipt, 
  Folder, 
  Bell,
  Settings,
  User,
  LogOut,
  Search,
  ChevronDown,
  FileText,
  Shield,
  Cog,
  HelpCircle,
  Package,
  MessageSquare,
  Server,
  Palette,
  Layers,
  Mail,
  BookOpen,
  Image,
  Tag,
  Building,
  Globe,
  FolderTree,
  Calendar,
  Menu as MenuIcon
} from 'lucide-react';
import { Cookie as CookieIcon, Bot, Key } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, adminUser } = useAdminAuth();

  // Rol bazlı menü filtreleme
  const getFilteredMenuItems = () => {
    const userRole = adminUser?.role || 'content_writer';
    
    let filtered = menuItems;
    
    if (userRole === 'admin') {
      // Admin her şeyi görebilir, sadece kullanıcılar için kontrol yap
      filtered = menuItems;
    } else if (userRole === 'editor') {
      // Editör: hizmetler, talepler, paketler, sayfa içerikleri, projeler, medya ve içerik yönetimi
      filtered = menuItems.filter(item =>
        [
          '/admin/services',
          '/admin/media',
          '/admin/quote-requests',
          '/admin/consultation-requests',
          '/admin/packages',
          '/admin/ecommerce-packages',
          '/admin/pro-ecommerce-packages',
          '/admin/b2b-packages',
          '/admin/ecommerce-designs',
          '/admin/projects',
          '/admin/page-contents',
          '/admin/content'
        ].includes(item.path)
      );
    } else if (userRole === 'content_writer') {
      // İçerik yazarı: medya ve içerik yönetimi
      filtered = menuItems.filter(item => 
        item.path === '/admin/content' || item.path === '/admin/media'
      );
    } else {
      filtered = [];
    }
    
    // Kullanıcılar menüsü sadece admin için görünür
    return filtered.filter(item => {
      if (item.path === '/admin/users') {
        return userRole === 'admin';
      }
      return true;
    });
  };

  const getFilteredSettingsMenu = () => {
    const userRole = adminUser?.role || 'content_writer';
    
    // Profil tüm roller için görünür
    const filteredMenu = settingsMenu.filter(item => {
      if (item.path === '/admin/profile') {
        return true; // Profil herkes için
      }
      // Genel Ayarlar sadece admin için
      return userRole === 'admin';
    });
    
    return filteredMenu;
  };

  const menuItems = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin',
      badge: null
    },
    {
      title: 'Medya',
      icon: Image,
      path: '/admin/media',
      badge: null
    },
    {
      title: 'Kullanıcılar',
      icon: Users,
      path: '/admin/users',
      badge: null
    },
    {
      title: 'Hizmetler',
      icon: Package,
      path: '/admin/services',
      badge: null
    },
    {
      title: 'Talepler',
      icon: MessageSquare,
      path: '/admin/quote-requests',
      badge: null,
      submenu: [
        {
          title: 'Teklif Talepleri',
          icon: MessageSquare,
          path: '/admin/quote-requests'
        },
        {
          title: 'Toplantı Talepleri',
          icon: Calendar,
          path: '/admin/meeting-requests'
        },
        {
          title: 'Teklif Oluştur',
          icon: FileText,
          path: '/admin/offer-requests'
        },
        {
          title: 'Ücretli Danışmanlık',
          icon: Receipt,
          path: '/admin/consultation-requests'
        },
        {
          title: 'E-posta Bülteni',
          icon: Mail,
          path: '/admin/newsletter'
        },
        {
          title: 'İşletmeler',
          icon: Building,
          path: '/admin/businesses'
        }
      ]
    },
    {
      title: 'Paketler',
      icon: Package,
      path: '/admin/packages',
      badge: null,
      submenu: [
        {
          title: 'VPS Paketleri',
          icon: Server,
          path: '/admin/vps-packages'
        },
        {
          title: 'VDS Sunucu Paketleri',
          icon: Server,
          path: '/admin/vds-packages'
        },
        {
          title: 'Kurumsal Sunucu Paketleri',
          icon: Server,
          path: '/admin/corporate-server-packages'
        },
        
        {
          title: 'Hosting Paketleri',
          icon: Package,
          path: '/admin/hosting-packages'
        },
        {
          title: 'WordPress Hosting Paketleri',
          icon: Package,
          path: '/admin/wordpress-hosting-packages'
        },
        {
          title: 'Kolay Site Paketleri',
          icon: Package,
          path: '/admin/kolay-site-packages'
        },
        {
          title: 'Kurumsal Web Sitesi Paketleri',
          icon: Package,
          path: '/admin/corporate-packages'
        },
        {
          title: 'Domain Fiyatları',
          icon: Globe,
          path: '/admin/domain-prices'
        },
        {
          title: 'E-Ticaret Hosting Paketleri',
          icon: Package,
          path: '/admin/ecommerce-hosting-packages'
        },
        
        {
          title: 'Bayi Hosting Paketleri',
          icon: Users,
          path: '/admin/reseller-hosting-packages'
        },
        {
          title: 'SSL Paketleri',
          icon: Shield,
          path: '/admin/ssl'
        },
        {
          title: 'E-ticaret Paketleri',
          icon: Package,
          path: '/admin/ecommerce-packages'
        },
        {
          title: 'Profesyonel E-Ticaret Paketleri',
          icon: Package,
          path: '/admin/pro-ecommerce-packages'
        },
        {
          title: 'B2B Paketleri',
          icon: Users,
          path: '/admin/b2b-packages'
        },
        {
          title: 'SEO Paketleri',
          icon: Package,
          path: '/admin/seo-packages'
        },
        {
          title: 'Reklam Paketleri',
          icon: Package,
          path: '/admin/advertising-packages'
        },
        {
          title: 'E-ticaret Genel Özellikler',
          icon: Shield,
          path: '/admin/ecommerce-features'
        }
      ]
    },
    // E-ticaret Paketleri grubu kaldırıldı; Paketler altına taşındı
        {
          title: 'Marka Tescil',
          icon: Tag,
          path: '/admin/marka-tescil',
          badge: null,
          submenu: [
            {
              title: 'Sınıflar',
              icon: Layers,
              path: '/admin/marka-tescil/classes'
            },
            {
              title: 'Başvurular',
              icon: FileText,
              path: '/admin/marka-tescil/applications'
            },
            {
              title: 'Ek Hizmetler',
              icon: Package,
              path: '/admin/marka-tescil/additional-services'
            }
          ]
        },
    {
      title: 'E-ticaret Tasarımları',
      icon: Palette,
      path: '/admin/ecommerce-designs',
      badge: null,
      submenu: [
        {
          title: 'E-ticaret Tasarımları',
          icon: Palette,
          path: '/admin/ecommerce-designs'
        },
        {
          title: 'E-ticaret Kategorileri',
          icon: Folder,
          path: '/admin/ecommerce-design-categories'
        },
        {
          title: 'Kolay Site Tasarımları',
          icon: Palette,
          path: '/admin/website-templates'
        },
        {
          title: 'Kolay Site Kategorileri',
          icon: Folder,
          path: '/admin/website-template-categories'
        },
        {
          title: 'Kurumsal Site Tasarımları',
          icon: Palette,
          path: '/admin/corporate-designs'
        },
        {
          title: 'Kurumsal Site Kategorileri',
          icon: Folder,
          path: '/admin/corporate-design-categories'
        }
      ]
    },
    // Kolay Site ana grubu kaldırıldı; 'Paketler' altına taşındı
    {
      title: 'Projeler',
      icon: Briefcase,
      path: '/admin/projects',
      badge: null,
      submenu: [
        {
          title: 'Projeler',
          icon: Briefcase,
          path: '/admin/projects'
        },
        {
          title: 'Kategoriler',
          icon: Folder,
          path: '/admin/project-categories'
        },
        {
          title: 'Alt Kategoriler',
          icon: Layers,
          path: '/admin/subcategories'
        }
      ]
    },
    {
      title: 'GogiAI Yönetimi',
      icon: Bot,
      path: '/admin/gogiai',
      badge: null,
      submenu: [
        {
          title: 'GogiAI Ayarları',
          icon: Bot,
          path: '/admin/gogiai'
        },
        {
          title: 'Konuşmalar',
          icon: MessageSquare,
          path: '/admin/gogiai-conversations'
        },
        {
          title: 'Müşteriler',
          icon: Users,
          path: '/admin/gogiai-customers'
        },
        {
          title: 'Hazır Cevaplar',
          icon: MessageSquare,
          path: '/admin/gogiai-quick-replies'
        }
      ]
    },
    {
      title: 'İçerik Yönetimi',
      icon: FileText,
      path: '/admin/content',
      badge: null,
      submenu: [
        {
          title: 'Blog Yazıları',
          icon: BookOpen,
          path: '/admin/blogs'
        },
        {
          title: 'Blog Kategorileri',
          icon: Tag,
          path: '/admin/blog-categories'
        },
        {
          title: 'SSS',
          icon: HelpCircle,
          path: '/admin/faq'
        },
        {
          title: 'Popup Yönetimi',
          icon: MessageSquare,
          path: '/admin/popup'
        },
        {
          title: 'Referanslar',
          icon: Building,
          path: '/admin/references'
        },
        {
          title: 'Referans Kategorileri',
          icon: FolderTree,
          path: '/admin/reference-categories'
        },
        {
          title: 'Eğitimler',
          icon: BookOpen,
          path: '/admin/educations'
        },
        {
          title: 'Eğitim Kategorileri',
          icon: Tag,
          path: '/admin/education-categories'
        },
        {
          title: 'Cookie',
          icon: CookieIcon,
          path: '/admin/cookies'
        },
        {
          title: 'KVKK',
          icon: FileText,
          path: '/admin/kvkk'
        },
        {
          title: 'E-posta Şablonları',
          icon: Mail,
          path: '/admin/email-templates'
        },
        {
          title: 'İndirim Kuponları',
          icon: Tag,
          path: '/admin/coupons'
        },
      ]
    },
    {
      title: 'Sayfa SEO Ayarları',
      icon: Globe,
      path: '/admin/pages',
      badge: null
    },
    {
      title: 'Sayfa İçerikleri',
      icon: FileText,
      path: '/admin/page-contents',
      badge: null
    },
    {
      title: 'Menü Yönetimi',
      icon: MenuIcon,
      path: '/admin/menus',
      badge: null
    },
    
  ];

  const settingsMenu = [
    {
      title: 'Profil',
      icon: User,
      path: '/admin/profile'
    },
    {
      title: 'Genel Ayarlar',
      icon: Settings,
      path: '/admin/settings'
    }
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:inset-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <Link to="/admin" className="flex items-center group">
              <div className="h-10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <img src="/website-al-logo.png" alt="Website Al Admin" className="w-[160px] h-full object-contain" />
              </div>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {getFilteredMenuItems().map((item) => (
              <div key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors group ${
                    isActive(item.path)
                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                  }`}
                  onClick={(e) => {
                    if (item.submenu) {
                      e.preventDefault();
                      setActiveSubmenu(activeSubmenu === item.path ? null : item.path);
                    } else {
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className={`w-5 h-5 ${
                      isActive(item.path) ? 'text-purple-600' : 'text-gray-500 group-hover:text-purple-600'
                    }`} />
                    <span className="font-medium">{item.title}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {item.badge && (
                      <Badge variant="secondary" className="text-xs">
                        {item.badge}
                      </Badge>
                    )}
                    {item.submenu && (
                      <ChevronDown className={`w-4 h-4 transition-transform ${
                        activeSubmenu === item.path ? 'rotate-180' : ''
                      }`} />
                    )}
                  </div>
                </Link>
                
                {/* Submenu */}
                {item.submenu && activeSubmenu === item.path && (
                  <div className="ml-4 mt-2 space-y-1">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                          isActive(subItem.path)
                            ? 'bg-purple-50 text-purple-700 border border-purple-200'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-purple-600'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <subItem.icon className={`w-4 h-4 ${
                          isActive(subItem.path) ? 'text-purple-600' : 'text-gray-400'
                        }`} />
                        <span className="text-sm font-medium">{subItem.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Settings Section */}
          <div className="p-4 border-t border-gray-200">
            <div className="space-y-2">
              {getFilteredSettingsMenu().map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors group ${
                    isActive(item.path)
                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`w-5 h-5 ${
                    isActive(item.path) ? 'text-purple-600' : 'text-gray-500 group-hover:text-purple-600'
                  }`} />
                  <span className="font-medium">{item.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </Button>
              
              <div className="hidden md:flex items-center space-x-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  {getFilteredMenuItems().find(item => isActive(item.path))?.title || 'Admin Panel'}
                </h1>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="hidden md:flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Ara..."
                  className="bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-500 w-48"
                />
              </div>

              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5" />
                <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs">
                  4
                </Badge>
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src="" />
                      <AvatarFallback className="bg-purple-100 text-purple-700">
                        {adminUser?.name?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-gray-900">
                        {adminUser?.name || 'Admin User'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {adminUser?.email || 'admin@website-al.com'}
                      </p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/admin/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    Profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Ayarlar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout; 